import { LuTerminal, LuArrowUpRight, LuSettings2, LuPlus, LuShare, LuUsers, LuGlobe } from 'react-icons/lu'
import { getHost, getServiceLabel } from '../utilities/service'
import { useNavigate } from 'react-router-dom'
import IconCopyButton from './IconCopyButton'
import ShareLinkButton from './ShareLinkButton'
import { inNativeApp } from '../lib/nativeApp'
import NodeData from './NodeData'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'

type NodeServicesProps = {
  services: Service[]
  nodeId: string
  // Whether the reader controls the node: its owner, or an org Owner/Admin. Only they publish,
  // change or delete its services (PERMISSIONS.md § 5 Services, app#373); the router refuses anyone
  // else, so the controls are not offered to them.
  canChange: boolean
  onOpenTerminal: (serviceName: string) => void
}

const NodeServices = ({ services, nodeId, canChange, onOpenTerminal }: NodeServicesProps) => {
  const navigate = useNavigate()

  return (
    <Box>
      <Typography variant="h6">Services</Typography>
      {services.length ? (
        services.map(service => {
          const terminal = !service.url
          const link = `https://${getHost(service)}`
          const label = getServiceLabel(service)
          return (
            <NodeData
              key={service.id}
              icon={terminal ? <LuTerminal /> : <LuArrowUpRight />}
              justifyIcon="center"
              data={[
                [
                  <Stack
                    direction="row"
                    component="span"
                    sx={{
                      gap: 1,
                      marginTop: 0.5
                    }}>
                    {!service.enabled && <Chip label="Disabled" size="small" sx={{ marginRight: 1 }} />}
                    {terminal ? (
                      <Button
                        fullWidth
                        size="small"
                        color="secondary"
                        variant="contained"
                        onClick={() => onOpenTerminal(service.name)}
                      >
                        {label}
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        size="small"
                        color="secondary"
                        variant="contained"
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {label}
                      </Button>
                    )}
                    <Stack direction="row" sx={{
                      justifyContent: 'end'
                    }}>
                      {/* In the app (mobile/): a QR code with Copy and the share sheet. In a
                          browser: copy the link. */}
                      {inNativeApp ? (
                        <ShareLinkButton url={link} title={label} />
                      ) : (
                        <IconCopyButton value={link} icon={<LuShare />} />
                      )}
                      {canChange && (
                        <IconButton
                          size="small"
                          sx={{ color: 'text.disabled', paddingX: 1 }}
                          onClick={() => navigate(`/service/${nodeId}/${service.id}`)}
                        >
                          <LuSettings2 />
                        </IconButton>
                      )}
                      {/* Deliberately the SAME route the router's access-request
                          email links to, so the mail and the app land on one
                          screen per service.
                          Always RENDERED, because an absent control reads as a
                          missing feature. A service with no auth has no access
                          list, so the icon carries the STATE — people for "these
                          people", globe for "everyone" — which is how LuEye/
                          LuEyeOff and LuLink2/LuLink2Off already read elsewhere;
                          greying alone would only say "unavailable" and leave
                          the reason to the tooltip. The span is required: a
                          disabled button emits no mouse events, so Tooltip needs
                          a live element to bind to (same as Terminal.tsx). */}
                      <Tooltip
                        title={service.authRequired ? 'Shared access' : 'Open to anyone — no access list'}
                        placement="top"
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!service.authRequired}
                            sx={{
                              color: 'text.disabled',
                              paddingX: 1,
                              '&.Mui-disabled': { color: 'contrast.low' },
                            }}
                            onClick={() => navigate(`/permissions/${service.name}`)}
                          >
                            {service.authRequired ? <LuUsers /> : <LuGlobe />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>,
                ],
              ]}
            />
          )
        })
      ) : (
        <NodeData icon={<LuArrowUpRight />} data={[['None']]} />
      )}
      <NodeData
        data={[
          [
            <Tooltip
              title={canChange ? '' : 'Only the owner of this node, or an organization owner or admin, can add a service'}
            >
              <span>
                <Button
                  startIcon={<LuPlus />}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  disabled={!canChange}
                  onClick={() => navigate(`/service/${nodeId}`)}
                >
                  Add Service
                </Button>
              </span>
            </Tooltip>,
          ],
        ]}
      />
    </Box>
  )
}

export default NodeServices
